import { useEffect, useId, useRef, useState } from 'react';

import { formatNumber } from '@shared/format';
import { SearchIcon } from '@shared/ui/icons/nav';

import { resolveEntitlementSource, resolveSubscriptionState } from '../_shared/constants';
import type { Plan } from '../_shared/types';
import { copy } from './copy';
import { ALL, hasAnyFilter, type LedgerFilters } from './filters';

/**
 * How long a keystroke waits before it becomes two network hops.
 *
 * The term costs an account resolve **and** a ledger read, so an undebounced
 * field issues eight requests for «سُهى». Three hundred milliseconds is the
 * middle of the engineering standard's 200–400 ms band for a search input: below
 * it the operator out-types the network, above it the table feels detached from
 * the field.
 */
const TERM_DEBOUNCE_MS = 300;

/**
 * The ledger's lookup bar — one field and three facets over one list.
 *
 * **The subscriber field is why this exists.** The ledger's own promise is «who
 * bought», and the surface answered it for whoever happened to be on page one:
 * the only route to a named person was the account register, which carries no
 * money row at all. That register link used to sit alone above this table — it
 * has not been dropped, it moved into the note directly above this bar, which is
 * where an operator is sent for the things this surface deliberately does not
 * do.
 *
 * **It is one panel at every width.** The four controls sit on a bordered
 * surface with a single grid inside it: one column at 390 px with every control
 * full-bleed, two from `sm`, and one four-track row from `lg` where the term
 * takes the wide track. Before that they were loose elements on the page ground
 * whose widths agreed with nothing — a capped field, a paragraph, and three
 * `11rem` selects that stayed `11rem` on a phone.
 *
 * **Three facets, and not one of them is invented.** The plan, the state and the
 * source are the three vocabularies the ledger already renders as columns; each
 * select offers exactly the values the surface can draw, and every one of them
 * is narrowed by the owning service rather than filtered out of a page here.
 * There is still **no period control and no date range** — that absence is a
 * standing area decision about a reporting surface, and searching for a person
 * is not a period.
 *
 * **The term applies as it is typed; the selects apply on change.** They are
 * different acts: a term is one continuous gesture that would leave a history
 * entry per keystroke, so it debounces and `replace`s, while choosing a plan is
 * a single decision that Back should undo. Neither needs a submit button, and
 * the field carries no keyboard trap — `Enter` in a lone search input would
 * submit a form that has nothing left to do.
 *
 * **Every facet lives in the address.** An operator working a subscriber hands
 * the URL to a colleague and they see the same rows; a reload keeps the
 * narrowing; and the ordinal beside them was already there.
 */
export function LedgerFilterBar({
  filters,
  plans,
  onChange,
  onClear,
  busy,
}: {
  filters: LedgerFilters;
  /** The plan vocabulary, as the money service defines it. Empty until it lands. */
  plans: readonly Plan[];
  onChange: (next: Partial<LedgerFilters>) => void;
  onClear: () => void;
  /** Whether a read is in flight — announced, never used to disable the field. */
  busy: boolean;
}) {
  const termId = useId();
  const planId = useId();
  const stateId = useId();
  const sourceId = useId();
  const termRef = useRef<HTMLInputElement>(null);

  /**
   * The field is uncontrolled by the URL between keystrokes.
   *
   * Typing writes to this state immediately and to the address bar on the
   * debounce, so the caret never jumps and the operator never types into a value
   * that is one render behind. The URL is still the source of truth: an address
   * arrived at from outside — a shared link, Back, the clear control — replaces
   * what is in the box.
   */
  const [draft, setDraft] = useState(filters.term);
  const committed = useRef(filters.term);

  useEffect(() => {
    if (filters.term === committed.current) return;
    committed.current = filters.term;
    setDraft(filters.term);
  }, [filters.term]);

  useEffect(() => {
    if (draft === committed.current) return;

    const timer = window.setTimeout(() => {
      committed.current = draft;
      onChange({ term: draft });
    }, TERM_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
    // `onChange` is re-created per render by the caller; depending on it would
    // restart the timer on every render and the term would never commit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  function clear() {
    committed.current = '';
    setDraft('');
    onClear();
    // The control that cleared may be the one inside the empty block, and that
    // block leaves the page the moment the clear succeeds. Focus lands on the
    // field rather than on `<body>`.
    termRef.current?.focus();
  }

  return (
    /*
      One panel, not four loose controls on the page ground.

      The bar used to be a bare stack: a capped field, a hint paragraph, then a
      `flex-wrap` row of content-width selects. Nothing tied the four together,
      so at every width they read as debris between the heading and the table —
      and at 390 px the selects kept their `11rem` content width under
      full-bleed labels, which is a narrow box hanging off the end of a wide
      label rather than a field. A bordered surface says «these four narrow one
      list», and the grid inside it gives every control the same edges.
    */
    <div className="mb-4 grid gap-4 rounded-md border border-border bg-surface-raised p-4 md:p-5">
      <p id={`${termId}-legend`} className="sr-only">
        {copy.filters.legend}
      </p>

      {/*
        Four steps, and the middle one is the reason there are four.

        One column below `sm`; two from `sm`; **three from `md`**; and one row of
        four tracks from `lg`. Without the `md` step a tablet gets two columns
        for three facets, which leaves the third alone on its own line beside an
        empty cell — a lone select under a full-width pair reads as a control
        that failed to load. Three facets want a three-track row, and 768 px has
        the width for one.

        At `lg` the term takes the wide track rather than an equal quarter: it is
        the control reached for first and the one holding the longest value, so
        `minmax(14rem,1.6fr)` against three `minmax(0,1fr)` facets. The
        `minmax(0,…)` is what keeps a long plan name from pushing its own column
        past its share — a bare `1fr` floors at min-content and the row then
        overflows the panel.
      */}
      <div className="grid gap-x-4 gap-y-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-[minmax(14rem,1.6fr)_repeat(3,minmax(0,1fr))] lg:items-end">
        <div className="grid gap-2 sm:col-span-2 md:col-span-3 lg:col-span-1">
          <label htmlFor={termId} className="text-sm font-medium text-ink">
            {copy.filters.term.label}
          </label>

          <div className="relative flex items-center">
            {/*
              Decorative: the label above already names the field, and a second
              reading of «ابحث» between the label and the value is noise.
            */}
            <SearchIcon aria-hidden className="pointer-events-none absolute ms-3 text-ink-faint" />
            {/*
              `ps-7`, not `ps-10`. **This repo's spacing scale is a step index, not
              a rem multiple**: `10` is the last step and resolves to `--space-10`
              = 6rem, so `ps-10` reserved 96px in front of an 18px glyph sitting
              12px from the edge and pushed the caret two-thirds of the way across
              the field. `7` is `--space-7` = 2.5rem, which is the glyph plus its
              inset plus one gap — the measurement the icon actually needs.
            */}
            <input
              id={termId}
              ref={termRef}
              type="search"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={copy.filters.term.placeholder}
              aria-describedby={`${termId}-legend ${termId}-hint`}
              className="hit-target w-full rounded-sm border border-border-strong bg-surface pe-3 ps-7 text-sm text-ink placeholder:text-ink-faint"
            />
          </div>
        </div>

        <Facet
          id={planId}
          label={copy.filters.plan.label}
          all={copy.filters.plan.all}
          value={filters.planId}
          options={plans.map((plan) => ({ value: plan.id, label: plan.name }))}
          onChange={(next) => onChange({ planId: next })}
        />

        <Facet
          id={stateId}
          label={copy.filters.state.label}
          all={copy.filters.state.all}
          value={filters.state}
          options={copy.filters.state.values.map((value) => ({
            value,
            label: resolveSubscriptionState(value).label,
          }))}
          onChange={(next) => onChange({ state: next })}
        />

        <Facet
          id={sourceId}
          label={copy.filters.source.label}
          all={copy.filters.source.all}
          value={filters.source}
          options={copy.filters.source.values.map((value) => ({
            value,
            label: resolveEntitlementSource(value),
          }))}
          onChange={(next) => onChange({ source: next })}
        />
      </div>

      {/*
        The hint and the clear control share the panel's last line, at opposite
        ends of it.

        The hint used to sit between the field and the selects, where it split
        one bar into two halves and pushed the facets a paragraph away from the
        thing they narrow. It belongs under the whole bar, because it explains
        the bar: the term resolves against accounts, and what comes back is what
        those accounts hold. `justify-between` with one child leaves the hint
        where it already was, so the row does not shift when the control
        appears.
      */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-border pt-3">
        <p id={`${termId}-hint`} className="mb-0 max-w-measure text-xs text-ink-muted">
          {copy.filters.term.hint}
        </p>

        {hasAnyFilter(filters) ? (
          <button
            type="button"
            onClick={clear}
            className="hit-target ms-auto rounded-sm border border-border-strong bg-surface px-4 text-sm font-semibold text-ink-muted hover:bg-hover-bg hover:text-ink"
          >
            {copy.filters.reset}
          </button>
        ) : null}
      </div>

      {/*
        One live region for the whole bar. The operator's attention is on the
        field they are typing in, not on the table, so the count is spoken rather
        than left for them to find — and it is `polite`, because a search result
        never interrupts.
      */}
      <p role="status" aria-live="polite" className="sr-only">
        {busy ? copy.filters.searching : ''}
      </p>
    </div>
  );
}

/**
 * One facet, as a native `<select>` that applies on change.
 *
 * Native rather than the console's own `Select`: that control is built for
 * authoring forms — it carries an invalid state, a describedby hint and a
 * blur handler a schema reads — and none of the three exists on a filter. A
 * filter has no wrong value.
 *
 * **It applies on change, with no «apply» button.** The register's role filter
 * has one because it sits in a `<form>` that a stray `Enter` would otherwise
 * submit; here every control is independent and applying immediately is one
 * fewer press between the operator and the rows.
 */
function Facet({
  id,
  label,
  all,
  value,
  options,
  onChange,
}: {
  id: string;
  label: string;
  all: string;
  value: string | null;
  options: readonly { value: string; label: string }[];
  onChange: (next: string | null) => void;
}) {
  return (
    <div className="grid gap-2">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      {/*
        `w-full`, and no `min-w`. The floor used to be `11rem`, which is wider
        than a 390 px column has to give once the panel's padding is taken out —
        the select then set its own width and sat short of the label above it.
        The grid track is the measurement now, and it is never narrower than the
        panel allows.
      */}
      <select
        id={id}
        value={value ?? ALL}
        onChange={(event) => onChange(event.target.value === ALL ? null : event.target.value)}
        className="hit-target w-full rounded-sm border border-border-strong bg-surface px-3 text-sm text-ink"
      >
        <option value={ALL}>{all}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * «‏٣ حسابات مطابِقة» — what the term resolved to, stated above the rows it
 * produced.
 *
 * Without it a searched ledger is ambiguous in the one way that matters: no rows
 * could mean «this person holds nothing» or «nobody is called that», and the two
 * send the operator to completely different places. The line names which.
 */
export function TermSummary({ matched, truncated }: { matched: number; truncated: boolean }) {
  return (
    <p className="mb-3 text-xs text-ink-muted">
      <bdi>
        {matched === 0 ? copy.filters.noAccounts : copy.filters.matchedAccounts(formatNumber(matched))}
      </bdi>
      {truncated ? <span className="ms-2">{copy.filters.truncated}</span> : null}
    </p>
  );
}
